import { useEffect, useState } from "react";
import { useReRender } from "./hooks";

type ExceptionObj = { message: string };
type DataState<TOut> = { data: TOut | null; error: ExceptionObj | null };

export interface UseDataOutput<TOut> {
	data: TOut | null;
	loading: boolean;
	isError: null | ExceptionObj;
	triggerRender: () => void;
}

export function useData<TIn, TOut>(
	fetchDataFn: (request: TIn, signal?: AbortSignal) => Promise<TOut>,
	params: TIn
): UseDataOutput<TOut> {
	const [dataState, setData] = useState<DataState<TOut>>({ data: null, error: null });
	const [loading, setLoading] = useState(true);

	const reRender = useReRender();

	useEffect(() => {
		const abortController = new AbortController();
		(async () => {
			setLoading(true);
			try {
				const resp = await fetchDataFn(params, abortController.signal);
				setData({ data: resp, error: null });
			} catch (e) {
				if (!abortController.signal.aborted) {
					console.error(`Error response for request. Inner error:\n\n${e}`);
					setData({ data: null, error: { message: `${e}` } });
				}
			} finally {
				if (!abortController.signal.aborted) {
					// no longer loading regardless of error or success.
					setLoading(false);
				}
			}
		})();
		return () => abortController.abort();
	}, [JSON.stringify(params), reRender.dependency]);

	return {
		data: dataState.data,
		loading,
		isError: dataState.error,
		triggerRender: reRender.triggerRender,
	};
}
